import {z} from 'zod';

/**
 * Configurable Accounts view.
 *
 * The Accounts page renders a kanban board (one column per "stage") and a table.
 * Internal staff can customize the stage names, sub-stage names, the ordered set
 * of stages (which drives the kanban column count), and which table columns show
 * — per client (`Client.features.accountsView`) and org-wide for the internal
 * portal (`InternalConfig.features.accountsView`).
 *
 * The customization is a *presentation layer* on top of a fixed canonical
 * backbone: the Prisma enums `LifecyclePhase` (5) and `AccountsSubStage` (27).
 * Every custom stage maps to one `LifecyclePhase` and every custom sub-stage to
 * one `AccountsSubStage`, so all server automation (RFC/MSA/CBS), transition
 * gating, and tab visibility keep operating on the canonical values untouched.
 *
 * `@common` cannot import the Prisma client, so the canonical value sets are
 * mirrored here as string unions. They must stay in sync with
 * `apps/api/prismas/prisma/models/accounts.prisma` and
 * `packages/common-web/src/components/account/phaseLabels.ts`.
 */

/* -------------------------------------------------------------------------- */
/*  Canonical backbone (mirrors the Prisma enums)                             */
/* -------------------------------------------------------------------------- */

// `PROPOSAL` is retained as a valid enum value for backward-compatibility with
// existing data and the Prisma `LifecyclePhase` enum, but it is no longer part
// of the active pipeline: the two proposal sub-stages now live inside the LEAD
// (Prospect) phase. See LIFECYCLE_PHASES / DEFAULT_PHASE_SUB_STAGES below.
export type LifecyclePhaseKey = 'LEAD' | 'PROPOSAL' | 'ONBOARDING' | 'IMPLEMENTATION' | 'ACTIVE';

export type AccountsSubStageKey =
  | 'NEW_LEAD'
  | 'SWOT_CALL'
  | 'PROPOSAL_SENT'
  | 'PROPOSAL_PRESENTATION'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'CLIENT_REVIEW'
  | 'CLIENT_APPROVED'
  | 'BROKER_REVIEW'
  | 'BROKER_APPROVED'
  | 'PROPOSAL'
  | 'MSA_PENDING'
  | 'MSA_SENT'
  | 'MSA_SIGNED'
  | 'SUBMITTED_FOR_IMPLEMENTATION'
  | 'RFC_RECEIVED'
  | 'WELCOME_CALL'
  | 'CENSUS_BUILD_OUT'
  | 'CENSUS_REVIEW'
  | 'CENSUS_UPDATE_NEEDED'
  | 'CENSUS_APPROVED'
  | 'IMPLEMENTATION_CALL'
  | 'DRY_RUN_1'
  | 'FINANCIAL_CALL'
  | 'DRY_RUN_2'
  | 'GO_LIVE_RUN'
  | 'ACTIVE';

// The active, ordered pipeline. `PROPOSAL` is intentionally excluded — proposals
// are now sub-stages of the LEAD (Prospect) phase, so there is no standalone
// Proposal column. Validation, full-phase coverage, and kanban columns all read
// from this array, so the Proposal phase no longer surfaces anywhere.
export const LIFECYCLE_PHASES: readonly LifecyclePhaseKey[] = [
  'LEAD',
  'ONBOARDING',
  'IMPLEMENTATION',
  'ACTIVE',
];

export const ACCOUNTS_SUB_STAGES: readonly AccountsSubStageKey[] = [
  'NEW_LEAD',
  'SWOT_CALL',
  'PROPOSAL_SENT',
  'PROPOSAL_PRESENTATION',
  'DRAFT',
  'SUBMITTED',
  'CLIENT_REVIEW',
  'CLIENT_APPROVED',
  'BROKER_REVIEW',
  'BROKER_APPROVED',
  'PROPOSAL',
  'MSA_PENDING',
  'MSA_SENT',
  'MSA_SIGNED',
  'SUBMITTED_FOR_IMPLEMENTATION',
  // Implementation order per the 2026-07-08 reconciliation: the Implementation
  // Call moved from right after RFC Received to right after Census Approved
  // (the formerly HealthCues-only order became the base order). WELCOME_CALL is
  // an opt-in step (Synrgy/Quantum) slotted right after RFC Received.
  'RFC_RECEIVED',
  'WELCOME_CALL',
  'CENSUS_BUILD_OUT',
  'CENSUS_REVIEW',
  'CENSUS_UPDATE_NEEDED',
  'CENSUS_APPROVED',
  'IMPLEMENTATION_CALL',
  'DRY_RUN_1',
  'FINANCIAL_CALL',
  'DRY_RUN_2',
  'GO_LIVE_RUN',
  'ACTIVE',
];

/**
 * True once the account has been sent to CBS for implementation — the
 * "Submitted for implementation" hand-off (or any canonical sub-stage after
 * it, covering tenants without the two-step hand-off, every Implementation
 * sub-stage, and Live). From that point CBS owns the record and the client-
 * portal Account Summary is locked; internal staff can still edit.
 */
export function isAccountSentToCbs(subStage: AccountsSubStageKey): boolean {
  return (
    ACCOUNTS_SUB_STAGES.indexOf(subStage) >=
    ACCOUNTS_SUB_STAGES.indexOf('SUBMITTED_FOR_IMPLEMENTATION')
  );
}

// Default display labels for each phase. The default config is built from these,
// and the admin editor seeds new stages with them.
export const DEFAULT_PHASE_LABELS: Record<LifecyclePhaseKey, string> = {
  LEAD: 'Prospect',
  // Vestigial — PROPOSAL is no longer an active phase (see LIFECYCLE_PHASES).
  PROPOSAL: 'Proposal',
  ONBOARDING: 'Onboarding',
  IMPLEMENTATION: 'Implementation',
  ACTIVE: 'Active',
};

export const DEFAULT_SUB_STAGE_LABELS: Record<AccountsSubStageKey, string> = {
  NEW_LEAD: 'Prospect',
  SWOT_CALL: 'SWOT Call',
  PROPOSAL_SENT: 'Quick Proposal Generated',
  PROPOSAL_PRESENTATION: 'Informed Analysis Generated',
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  CLIENT_REVIEW: 'Client Review',
  CLIENT_APPROVED: 'Client Approved',
  BROKER_REVIEW: 'Broker Review',
  BROKER_APPROVED: 'Broker Approved',
  PROPOSAL: 'Proposal',
  MSA_PENDING: 'MSA Pending',
  MSA_SENT: 'MSA Sent',
  MSA_SIGNED: 'MSA Signed',
  SUBMITTED_FOR_IMPLEMENTATION: 'Submitted for implementation',
  RFC_RECEIVED: 'RFC Received',
  WELCOME_CALL: 'Welcome Call',
  CENSUS_BUILD_OUT: 'Census / API Build Out',
  CENSUS_REVIEW: 'Census Review',
  CENSUS_UPDATE_NEEDED: 'Census Update Needed',
  CENSUS_APPROVED: 'Census Approved',
  IMPLEMENTATION_CALL: 'Implementation Call',
  DRY_RUN_1: 'Dry Run #1',
  FINANCIAL_CALL: 'Financial Call',
  DRY_RUN_2: 'Dry Run #2',
  GO_LIVE_RUN: 'Go Live Run',
  ACTIVE: 'Active',
};

// Ordered canonical sub-stages within each phase. Mirrors PHASE_SUB_STAGES in
// phaseLabels.ts and SUB_STAGE_PHASE in accountsLifecycle.ts.
export const DEFAULT_PHASE_SUB_STAGES: Record<LifecyclePhaseKey, AccountsSubStageKey[]> = {
  // The proposal sub-stages now live inside the Prospect (LEAD) phase, so a
  // prospect flows Prospect → Quick proposal generated → Informed Analysis
  // Generated in a single column. SWOT_CALL is layered in via
  // OPT_IN_SUB_STAGE_PHASE for Synrgy.
  LEAD: ['NEW_LEAD', 'PROPOSAL_SENT', 'PROPOSAL_PRESENTATION'],
  // Vestigial — kept only so the Record stays total over LifecyclePhaseKey.
  // PROPOSAL is not in LIFECYCLE_PHASES, so this array is never iterated.
  PROPOSAL: [],
  ONBOARDING: [
    'DRAFT',
    'SUBMITTED',
    'CLIENT_REVIEW',
    'CLIENT_APPROVED',
    'BROKER_REVIEW',
    'BROKER_APPROVED',
    'MSA_PENDING',
    'MSA_SENT',
    'MSA_SIGNED',
  ],
  // 2026-07-08 reconciliation order: Census Approved closes the census loop and
  // the Implementation Call follows it (for every tenant). WELCOME_CALL is
  // layered in via OPT_IN_SUB_STAGE_PHASE for Synrgy/Quantum.
  IMPLEMENTATION: [
    'RFC_RECEIVED',
    'CENSUS_BUILD_OUT',
    'CENSUS_REVIEW',
    'CENSUS_UPDATE_NEEDED',
    'CENSUS_APPROVED',
    'IMPLEMENTATION_CALL',
    'DRY_RUN_1',
    'FINANCIAL_CALL',
    'DRY_RUN_2',
    'GO_LIVE_RUN',
  ],
  ACTIVE: ['ACTIVE'],
};

// Canonical phase for sub-stages that exist in the enum but are intentionally
// NOT part of the default visible pipeline — opt-in per tenant via their custom
// accountsView config (e.g. SWOT_CALL, surfaced only for Synrgy). Keeping them
// out of DEFAULT_PHASE_SUB_STAGES means the default pipeline (and therefore
// every unconfigured tenant) is unchanged, while still giving validation /
// position resolution a canonical phase to map them to.
const OPT_IN_SUB_STAGE_PHASE: Partial<Record<AccountsSubStageKey, LifecyclePhaseKey>> = {
  SWOT_CALL: 'LEAD',
  // Opt-in onboarding sub-stage: a single "Proposal" step generated AFTER RFC
  // approval (Synrgy). Kept out of DEFAULT_PHASE_SUB_STAGES so unconfigured
  // tenants never see it; still needs a canonical phase for validation/mapping.
  PROPOSAL: 'ONBOARDING',
  // Closing onboarding sub-stage, surfaced only for tenants whose config opts
  // into the two-step CBS handoff (e.g. the Synrgy demo onboarding). Kept out of
  // DEFAULT_PHASE_SUB_STAGES so every unconfigured tenant keeps the single-step
  // submit (MSA Signed → RFC Received) untouched.
  SUBMITTED_FOR_IMPLEMENTATION: 'ONBOARDING',
  // Implementation step right after RFC Received, for Synrgy and Quantum only
  // (gated by the tenant `welcomeCall` feature flag — see
  // resolveWelcomeCallEnabled in menuFlags.ts). Kept out of
  // DEFAULT_PHASE_SUB_STAGES so HealthCues/Champ never see it; the internal
  // ladder includes it and the lifecycle band hides it per account.
  WELCOME_CALL: 'IMPLEMENTATION',
};

// Canonical sub-stage → phase. Derived from DEFAULT_PHASE_SUB_STAGES (so it can
// never drift from the grouping above) plus the opt-in overlay for sub-stages
// that live outside the default pipeline.
export const CANONICAL_SUB_STAGE_PHASE: Record<AccountsSubStageKey, LifecyclePhaseKey> = {
  ...(Object.fromEntries(
    LIFECYCLE_PHASES.flatMap((phase) =>
      DEFAULT_PHASE_SUB_STAGES[phase].map((sub) => [sub, phase] as const),
    ),
  ) as Record<AccountsSubStageKey, LifecyclePhaseKey>),
  ...OPT_IN_SUB_STAGE_PHASE,
};

/* -------------------------------------------------------------------------- */
/*  RFC gate                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * THE RFC GATE — the canonical sub-stage an account may not move to or past
 * until an RFC has been PROVIDED. CLIENT_APPROVED is the canonical
 * "RFC Approved" milestone (tenants with a post-approval Proposal step land on
 * PROPOSAL, which sits after it in the canonical order, so it is covered too).
 *
 * "Provided" is deliberately NOT a required-field completeness check (mirrors
 * the synrgyinfinity 2026-07-24 gate change): any uploaded RFC PDF or a
 * digital form that has actually been worked on clears it. The server enforces
 * the rule (`accountRfcProvided` in `apps/api/src/lib/accountsLifecycle.ts`);
 * the Move-group modal surfaces the same gate as a UI convenience.
 */
export const RFC_GATE_SUB_STAGE: AccountsSubStageKey = 'CLIENT_APPROVED';

/**
 * Whether a move from `current` to `target` crosses the RFC gate — i.e. the
 * account currently sits before the canonical RFC-approved milestone and the
 * destination is at or past it. Uses the canonical sub-stage order, so it
 * holds regardless of which sub-stages a tenant's view config represents.
 */
export function crossesRfcGate(current: AccountsSubStageKey, target: AccountsSubStageKey): boolean {
  const gateIdx = ACCOUNTS_SUB_STAGES.indexOf(RFC_GATE_SUB_STAGE);
  return (
    ACCOUNTS_SUB_STAGES.indexOf(target) >= gateIdx && ACCOUNTS_SUB_STAGES.indexOf(current) < gateIdx
  );
}

/* -------------------------------------------------------------------------- */
/*  Table columns                                                             */
/* -------------------------------------------------------------------------- */

export type AccountsViewTableColumnKey =
  | 'client'
  | 'company'
  | 'phase'
  | 'subStage'
  | 'groupNumber'
  | 'members'
  | 'state'
  | 'owner'
  | 'dealValue'
  | 'nextAction'
  | 'lastActivity';

export type AccountsViewTableColumnDef = {
  key: AccountsViewTableColumnKey;
  label: string;
};

// Full table-column catalog in display order — the order here IS the render
// order of the internal Accounts table (stored `tableColumns` configs are a
// subset persisted in this order). Sequenced so the default internal set reads
// like the cbs-portal demo table: Group / Stage / Next Action / Broker /
// State / Members. `company` is intentionally not configurable-off (a row
// needs an identity), but it is included so the editor can render it as a
// locked row.
//
// `client` (which tenant the account belongs to) and `groupNumber` (the VBA
// group id once the account becomes a group) exist primarily for the internal
// portal, whose Accounts list spans every client — they let internal staff
// reproduce the cross-tenant CBS accounts view. They're harmless (and simply
// opt-in) on single-tenant client portals.
//
// The `owner` column renders the account's assigned broker. The key stays
// `'owner'` because it's persisted in stored `tableColumns` configs; only the
// display label says "Assigned Broker".
export const ACCOUNTS_VIEW_TABLE_COLUMNS: readonly AccountsViewTableColumnDef[] = [
  {key: 'client', label: 'Client'},
  {key: 'company', label: 'Company'},
  {key: 'phase', label: 'Phase'},
  {key: 'subStage', label: 'Sub-stage'},
  {key: 'groupNumber', label: 'Group #'},
  {key: 'nextAction', label: 'Next Action'},
  {key: 'owner', label: 'Assigned Broker'},
  {key: 'state', label: 'State'},
  {key: 'members', label: 'Members'},
  {key: 'dealValue', label: 'Deal Value'},
  {key: 'lastActivity', label: 'Last Activity'},
];

export const DEFAULT_TABLE_COLUMN_KEYS: readonly AccountsViewTableColumnKey[] =
  ACCOUNTS_VIEW_TABLE_COLUMNS.map((c) => c.key);

/* -------------------------------------------------------------------------- */
/*  Config shape                                                              */
/* -------------------------------------------------------------------------- */

export type AccountsViewSubStage = {
  /** Stable slug, unique within the config. */
  key: string;
  label: string;
  /** Canonical sub-stage this custom sub-stage maps to (drives automation). */
  systemSubStage: AccountsSubStageKey;
};

export type AccountsViewStage = {
  /** Stable slug, unique within the config. */
  key: string;
  label: string;
  /** Canonical lifecycle phase this custom stage maps to (drives gating). */
  systemPhase: LifecyclePhaseKey;
  description?: string;
  /** Ordered sub-stages within this stage (at least one). */
  subStages: AccountsViewSubStage[];
  /**
   * Hidden stages stay fully configured but are removed from every render
   * surface (kanban lane, status-board card) and their accounts are excluded
   * from the table rows + counts. Purely presentational and reversible; the
   * canonical backbone and automation are unaffected.
   */
  hidden?: boolean;
};

export type AccountsViewConfig = {
  /** Ordered stages. The length is the kanban column count. */
  stages: AccountsViewStage[];
  /** Visible table columns (in `ACCOUNTS_VIEW_TABLE_COLUMNS` order). Undefined = all. */
  tableColumns?: AccountsViewTableColumnKey[];
};

/* -------------------------------------------------------------------------- */
/*  Default config (current hardcoded pipeline)                               */
/* -------------------------------------------------------------------------- */

export const DEFAULT_ACCOUNTS_VIEW_CONFIG: AccountsViewConfig = {
  stages: LIFECYCLE_PHASES.map((phase) => ({
    key: phase,
    label: DEFAULT_PHASE_LABELS[phase],
    systemPhase: phase,
    subStages: DEFAULT_PHASE_SUB_STAGES[phase].map((sub) => ({
      key: sub,
      label: DEFAULT_SUB_STAGE_LABELS[sub],
      systemSubStage: sub,
    })),
  })),
};

/**
 * Default org-wide internal-portal pipeline. Implementation-forward only: one
 * kanban column per granular CBS workflow status plus an Active column. CBS staff
 * work accounts from RFC Received onward; earlier-phase accounts are excluded
 * from the internal Accounts view (see representedPhases / the phase filter in
 * account.list). Seed it onto InternalConfig.features.accountsView; it remains
 * editable in the internal admin editor.
 */
export const INTERNAL_DEFAULT_ACCOUNTS_VIEW_CONFIG: AccountsViewConfig = {
  stages: [
    {
      key: 'rfc-received',
      label: 'RFC Received',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'rfc-received', label: 'RFC Received', systemSubStage: 'RFC_RECEIVED'}],
    },
    // Synrgy/Quantum-only step (tenant `welcomeCall` flag). The column exists
    // for every internal user — the board is cross-tenant — but the lifecycle
    // band hides it on accounts whose tenant has the flag off (HealthCues,
    // Champ), and only flag-on accounts ever land here.
    {
      key: 'welcome-call',
      label: 'Welcome Call',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'welcome-call', label: 'Welcome Call', systemSubStage: 'WELCOME_CALL'}],
    },
    {
      key: 'census-build-out',
      label: 'Census / API Build Out',
      systemPhase: 'IMPLEMENTATION',
      subStages: [
        {
          key: 'census-build-out',
          label: 'Census / API Build Out',
          systemSubStage: 'CENSUS_BUILD_OUT',
        },
      ],
    },
    {
      key: 'census-review',
      label: 'Census Review',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'census-review', label: 'Census Review', systemSubStage: 'CENSUS_REVIEW'}],
    },
    {
      key: 'census-update',
      label: 'Census Update Needed',
      systemPhase: 'IMPLEMENTATION',
      subStages: [
        {
          key: 'census-update',
          label: 'Census Update Needed',
          systemSubStage: 'CENSUS_UPDATE_NEEDED',
        },
      ],
    },
    {
      key: 'census-approved',
      label: 'Census Approved',
      systemPhase: 'IMPLEMENTATION',
      subStages: [
        {key: 'census-approved', label: 'Census Approved', systemSubStage: 'CENSUS_APPROVED'},
      ],
    },
    // Sits after Census Approved (not after RFC Received) per the 2026-07-08
    // reconciliation — the formerly HealthCues-only order is now the base order.
    {
      key: 'implementation-call',
      label: 'Implementation Call',
      systemPhase: 'IMPLEMENTATION',
      subStages: [
        {
          key: 'implementation-call',
          label: 'Implementation Call',
          systemSubStage: 'IMPLEMENTATION_CALL',
        },
      ],
    },
    {
      key: 'dry-run-1',
      label: 'Dry Run #1',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'dry-run-1', label: 'Dry Run #1', systemSubStage: 'DRY_RUN_1'}],
    },
    {
      key: 'financial-call',
      label: 'Financial Call',
      systemPhase: 'IMPLEMENTATION',
      subStages: [
        {key: 'financial-call', label: 'Financial Call', systemSubStage: 'FINANCIAL_CALL'},
      ],
    },
    {
      key: 'dry-run-2',
      label: 'Dry Run #2',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'dry-run-2', label: 'Dry Run #2', systemSubStage: 'DRY_RUN_2'}],
    },
    {
      key: 'go-live-run',
      label: 'Go Live Run',
      systemPhase: 'IMPLEMENTATION',
      subStages: [{key: 'go-live-run', label: 'Go Live Run', systemSubStage: 'GO_LIVE_RUN'}],
    },
    {
      key: 'active',
      label: 'Active',
      systemPhase: 'ACTIVE',
      subStages: [{key: 'active', label: 'Active', systemSubStage: 'ACTIVE'}],
    },
  ],
  // The demo table's exact column set: Group / Stage / Next Action / Broker /
  // State / Members (`client` renders as the tenant pill inside the Company
  // cell, not as a standalone column). Render order comes from the catalog.
  tableColumns: ['client', 'company', 'subStage', 'nextAction', 'owner', 'state', 'members'],
};

/**
 * Resolve the active config from a features blob. Returns the configured value
 * when it has at least one stage, otherwise the scope's default pipeline:
 * the granular implementation-forward pipeline for the internal portal, the
 * coarse 5-stage journey for client portals. The fallback keeps behavior
 * identical for tenants that have never been configured.
 */
export function resolveAccountsViewConfig(
  accountsView: AccountsViewConfig | null | undefined,
  scope: 'client' | 'internal' = 'client',
): AccountsViewConfig {
  if (accountsView && Array.isArray(accountsView.stages) && accountsView.stages.length > 0) {
    return accountsView;
  }
  return scope === 'internal'
    ? INTERNAL_DEFAULT_ACCOUNTS_VIEW_CONFIG
    : DEFAULT_ACCOUNTS_VIEW_CONFIG;
}

/**
 * The canonical phases this config represents, in canonical order. A view only
 * shows accounts in these phases — the internal portal uses this to scope its
 * Accounts view to Implementation-forward (see account.list / stageCounts).
 */
export function representedPhases(config: AccountsViewConfig): LifecyclePhaseKey[] {
  const present = new Set(config.stages.map((s) => s.systemPhase));
  return LIFECYCLE_PHASES.filter((p) => present.has(p));
}

/** Stages that should render (hidden stages are kept in config but not shown). */
export function visibleStages(config: AccountsViewConfig): AccountsViewStage[] {
  return config.stages.filter((s) => !s.hidden);
}

/**
 * Canonical sub-stages that belong only to hidden stages (not represented by
 * any visible stage). The server excludes these from list/counts so hidden
 * stages' accounts drop out everywhere. Sub-stages shared with a visible stage
 * are intentionally NOT excluded (union/visible-wins semantics).
 */
export function hiddenCanonicalSubStages(config: AccountsViewConfig): AccountsSubStageKey[] {
  const visible = new Set<AccountsSubStageKey>();
  const hidden = new Set<AccountsSubStageKey>();
  for (const stage of config.stages) {
    const target = stage.hidden ? hidden : visible;
    for (const ss of stage.subStages) target.add(ss.systemSubStage);
  }
  return [...hidden].filter((s) => !visible.has(s));
}

/* -------------------------------------------------------------------------- */
/*  Resolution helpers                                                        */
/* -------------------------------------------------------------------------- */

export type ResolvedAccountPosition = {
  stage: AccountsViewStage;
  subStage: AccountsViewSubStage;
};

/** Find a sub-stage (and its parent stage) by config key. */
export function findSubStageByKey(
  config: AccountsViewConfig,
  subStageKey: string | null | undefined,
): ResolvedAccountPosition | null {
  if (!subStageKey) return null;
  for (const stage of config.stages) {
    const subStage = stage.subStages.find((s) => s.key === subStageKey);
    if (subStage) return {stage, subStage};
  }
  return null;
}

/**
 * First config stage mapped to the given canonical phase. Prefers a visible
 * stage so resolution agrees with the visible-wins server exclusion; falls back
 * to a hidden stage only when the phase has no visible stage.
 */
export function findStageBySystemPhase(
  config: AccountsViewConfig,
  systemPhase: LifecyclePhaseKey,
): AccountsViewStage | null {
  return (
    config.stages.find((s) => s.systemPhase === systemPhase && !s.hidden) ??
    config.stages.find((s) => s.systemPhase === systemPhase) ??
    null
  );
}

/**
 * Canonical sub-stage an account should land on when it first enters `phase`
 * under this config — the `systemSubStage` of the first sub-stage of the first
 * (visible-preferred) stage mapped to that phase. Lets automation that advances
 * an account into a phase honor the tenant's configured entry sub-stage (e.g.
 * the Synrgy demo's "RFC Draft") instead of hardcoding a canonical literal.
 * Falls back to `fallback` when the config has no stage for the phase.
 */
export function firstSubStageOfPhase(
  config: AccountsViewConfig,
  phase: LifecyclePhaseKey,
  fallback: AccountsSubStageKey,
): AccountsSubStageKey {
  return findStageBySystemPhase(config, phase)?.subStages[0]?.systemSubStage ?? fallback;
}

/**
 * First config sub-stage (and stage) mapped to the given canonical sub-stage.
 * Prefers a visible stage (visible-wins) when several stages map the same
 * canonical value, falling back to a hidden stage only if none is visible.
 */
export function findPositionBySystemSubStage(
  config: AccountsViewConfig,
  systemSubStage: AccountsSubStageKey,
): ResolvedAccountPosition | null {
  let hiddenMatch: ResolvedAccountPosition | null = null;
  for (const stage of config.stages) {
    const subStage = stage.subStages.find((s) => s.systemSubStage === systemSubStage);
    if (subStage) {
      if (!stage.hidden) return {stage, subStage};
      hiddenMatch ??= {stage, subStage};
    }
  }
  return hiddenMatch;
}

/**
 * Resolve where an account sits in the active config. Prefers the explicitly
 * stored `viewSubStageKey` (set when a custom config is finer-grained than the
 * canonical backbone); otherwise derives the position from the canonical
 * `subStage`, and finally from the canonical `lifecyclePhase`. Returns null when
 * the account's phase isn't represented in the config (callers must treat the
 * account as outside this view).
 */
export function resolveAccountPosition(
  config: AccountsViewConfig,
  account: {
    lifecyclePhase: LifecyclePhaseKey;
    subStage: AccountsSubStageKey;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
  },
): ResolvedAccountPosition | null {
  // Honor a stored key only if it's still consistent with the canonical
  // sub-stage AND its stage is visible. Server automation moves the canonical
  // `subStage` without touching the view keys, so a stale key whose
  // `systemSubStage` no longer matches must be ignored (canonical is the source
  // of truth). A stored key pointing at a hidden stage is likewise ignored so a
  // shared-sub-stage account resolves to the visible stage the server keeps it
  // under (visible-wins).
  const byStoredKey = findSubStageByKey(config, account.viewSubStageKey);
  if (
    byStoredKey &&
    !byStoredKey.stage.hidden &&
    byStoredKey.subStage.systemSubStage === account.subStage
  ) {
    return byStoredKey;
  }

  const bySystemSubStage = findPositionBySystemSubStage(config, account.subStage);
  if (bySystemSubStage) return bySystemSubStage;

  const stage = findStageBySystemPhase(config, account.lifecyclePhase);
  if (stage && stage.subStages.length > 0) {
    return {stage, subStage: stage.subStages[0]};
  }

  // No last resort: an account whose phase isn't represented in the config is
  // intentionally invisible to this view (e.g. a LEAD account under an
  // implementation-forward internal config). Callers must skip null positions.
  return null;
}

/**
 * Map a canonical sub-stage to the config keys that should be persisted on the
 * account (`viewStageKey` / `viewSubStageKey`). Returns null when the canonical
 * value isn't represented in the config (caller should leave the keys cleared
 * and fall back to canonical-derived display).
 */
export function mapCanonicalSubStageToViewKeys(
  config: AccountsViewConfig,
  systemSubStage: AccountsSubStageKey,
): {viewStageKey: string; viewSubStageKey: string} | null {
  const pos = findPositionBySystemSubStage(config, systemSubStage);
  if (!pos) return null;
  return {viewStageKey: pos.stage.key, viewSubStageKey: pos.subStage.key};
}

/**
 * The systemSubStage of the sub-stage immediately following `systemSubStage`
 * within the same stage of `config`. Returns null when it is the last sub-stage
 * of its stage (or absent). Used by saveProposal to advance Proposal → the next
 * onboarding step in a config-driven way.
 */
export function nextSystemSubStage(
  config: AccountsViewConfig,
  systemSubStage: AccountsSubStageKey,
): AccountsSubStageKey | null {
  for (const stage of config.stages) {
    const idx = stage.subStages.findIndex((s) => s.systemSubStage === systemSubStage);
    if (idx === -1) continue;
    const next = stage.subStages[idx + 1];
    return next ? next.systemSubStage : null;
  }
  return null;
}

/**
 * The pipeline a mover steps through, flattened to unique canonical sub-stages
 * in display order. WELCOME_CALL is withheld when the account's tenant doesn't
 * have the step (mirrors the lifecycle band / Move-stage modal hiding it).
 */
function flattenPipelineSubStages(
  config: AccountsViewConfig,
  welcomeCallEnabled: boolean,
): AccountsSubStageKey[] {
  const seen = new Set<AccountsSubStageKey>();
  const flat: AccountsSubStageKey[] = [];
  for (const stage of config.stages) {
    for (const sub of stage.subStages) {
      const sys = sub.systemSubStage;
      if (sys === 'WELCOME_CALL' && !welcomeCallEnabled) continue;
      if (seen.has(sys)) continue;
      seen.add(sys);
      flat.push(sys);
    }
  }
  return flat;
}

/**
 * The sub-stages a forward move from `current` to `target` passes over —
 * strictly between the two in the pipeline the mover sees. Every returned
 * stage requires an explicit resolution (a recorded date or a skip with a
 * reason) before the move may proceed.
 *
 * Returns `[]` for backward, same-stage, and adjacent moves. When the active
 * config doesn't represent both endpoints (e.g. the implementation-forward
 * internal pipeline moving a Lead-phase account), the default full pipeline is
 * used, falling back to the canonical order as a last resort — the same
 * fallback chain the band/modal render against, so the asked-about steps always
 * match what the user sees.
 */
export function forwardJumpSubStages(
  config: AccountsViewConfig,
  current: AccountsSubStageKey,
  target: AccountsSubStageKey,
  opts?: {welcomeCallEnabled?: boolean},
): AccountsSubStageKey[] {
  const welcomeCallEnabled = opts?.welcomeCallEnabled ?? true;
  const candidates: AccountsSubStageKey[][] = [
    flattenPipelineSubStages(config, welcomeCallEnabled),
    flattenPipelineSubStages(DEFAULT_ACCOUNTS_VIEW_CONFIG, welcomeCallEnabled),
    ACCOUNTS_SUB_STAGES.filter((s) => s !== 'WELCOME_CALL' || welcomeCallEnabled),
  ];
  for (const flat of candidates) {
    const fromIdx = flat.indexOf(current);
    const toIdx = flat.indexOf(target);
    if (fromIdx === -1 || toIdx === -1) continue;
    if (toIdx <= fromIdx) return [];
    return flat.slice(fromIdx + 1, toIdx);
  }
  return [];
}

/**
 * Whether a move from `current` to `target` goes forward in the canonical
 * pipeline order. Forward moves through the Move-stage / board surfaces
 * collect an effective date (and per-stage resolutions when stages are passed
 * over); backward moves collect nothing.
 */
export function isForwardSubStageMove(
  current: AccountsSubStageKey,
  target: AccountsSubStageKey,
): boolean {
  return ACCOUNTS_SUB_STAGES.indexOf(target) > ACCOUNTS_SUB_STAGES.indexOf(current);
}

/**
 * The systemSubStage immediately following `systemSubStage` in overall pipeline
 * order — crossing stage boundaries (unlike `nextSystemSubStage`, which only
 * looks within the same stage). Returns null when it is the very last sub-stage
 * of the pipeline (or absent). Used to detect config shapes that split a single
 * logical step across separate one-item stages (e.g. Synrgy's Prospect +
 * SWOT Call).
 */
export function subStageAfterInPipeline(
  config: AccountsViewConfig,
  systemSubStage: AccountsSubStageKey,
): AccountsSubStageKey | null {
  const flat = config.stages.flatMap((stage) => stage.subStages);
  const idx = flat.findIndex((s) => s.systemSubStage === systemSubStage);
  if (idx === -1) return null;
  const next = flat[idx + 1];
  return next ? next.systemSubStage : null;
}

/* -------------------------------------------------------------------------- */
/*  Validation                                                                */
/* -------------------------------------------------------------------------- */

const lifecyclePhaseSchema = z.enum(
  LIFECYCLE_PHASES as unknown as [LifecyclePhaseKey, ...LifecyclePhaseKey[]],
);
export const accountsSubStageSchema = z.enum(
  ACCOUNTS_SUB_STAGES as unknown as [AccountsSubStageKey, ...AccountsSubStageKey[]],
);
const tableColumnSchema = z.enum(
  DEFAULT_TABLE_COLUMN_KEYS as unknown as [
    AccountsViewTableColumnKey,
    ...AccountsViewTableColumnKey[],
  ],
);

// A config key must be a non-empty, slug-friendly string so it can be persisted
// on the account row and round-tripped through the editor.
const configKeySchema = z
  .string()
  .trim()
  .min(1, 'Key is required')
  .max(64)
  .regex(/^[a-zA-Z0-9_-]+$/, 'Key may only contain letters, numbers, dashes and underscores');

const accountsViewSubStageSchema = z.object({
  key: configKeySchema,
  label: z.string().trim().min(1, 'Label is required').max(80),
  systemSubStage: accountsSubStageSchema,
});

const accountsViewStageSchema = z.object({
  key: configKeySchema,
  label: z.string().trim().min(1, 'Label is required').max(80),
  systemPhase: lifecyclePhaseSchema,
  description: z.string().trim().max(280).optional(),
  subStages: z.array(accountsViewSubStageSchema).min(1, 'A stage needs at least one sub-stage'),
  hidden: z.boolean().optional(),
});

function buildAccountsViewConfigSchema(opts: {requireFullPhaseCoverage: boolean}) {
  return z
    .object({
      stages: z.array(accountsViewStageSchema).min(1, 'Add at least one stage'),
      tableColumns: z.array(tableColumnSchema).optional(),
    })
    .superRefine((config, ctx) => {
      if (config.stages.every((s) => s.hidden)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one stage must stay visible',
          path: ['stages'],
        });
      }

      const stageKeys = new Set<string>();
      const subStageKeys = new Set<string>();
      config.stages.forEach((stage, stageIndex) => {
        if (stageKeys.has(stage.key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate stage key "${stage.key}"`,
            path: ['stages'],
          });
        }
        stageKeys.add(stage.key);
        stage.subStages.forEach((sub, subIndex) => {
          if (subStageKeys.has(sub.key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate sub-stage key "${sub.key}"`,
              path: ['stages'],
            });
          }
          subStageKeys.add(sub.key);

          // A sub-stage's canonical phase must match its parent stage's
          // declared phase, otherwise an account positioned there would have a
          // canonical `subStage`/`lifecyclePhase` pair that contradicts the
          // stage it renders under (and `resolveAccountPosition` /
          // `mapCanonicalSubStageToViewKeys` would disagree with the editor).
          const canonicalPhase = CANONICAL_SUB_STAGE_PHASE[sub.systemSubStage];
          if (canonicalPhase !== stage.systemPhase) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Sub-stage "${sub.label}" belongs to phase "${canonicalPhase}" but its stage declares "${stage.systemPhase}"`,
              path: ['stages', stageIndex, 'subStages', subIndex],
            });
          }
        });
      });

      if (opts.requireFullPhaseCoverage) {
        const phases = new Set(config.stages.map((s) => s.systemPhase));
        for (const phase of LIFECYCLE_PHASES) {
          if (!phases.has(phase)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Every lifecycle phase must be represented by a stage; missing "${phase}"`,
              path: ['stages'],
            });
          }
        }
      }
    });
}

/**
 * Per-client (tenant) configs must represent every canonical phase: client
 * users follow the whole journey (lead → active), so automation always has a
 * visible stage to land on.
 */
export const accountsViewConfigSchema = buildAccountsViewConfigSchema({
  requireFullPhaseCoverage: true,
});

/**
 * The org-wide internal-portal config may scope the pipeline to a subset of
 * phases — CBS staff work accounts from Implementation forward. Accounts in
 * unrepresented phases are excluded from the internal Accounts view entirely
 * (see `representedPhases` and the phase filter in account.list/stageCounts).
 */
export const internalAccountsViewConfigSchema = buildAccountsViewConfigSchema({
  requireFullPhaseCoverage: false,
});
